import { useState } from 'react';
import { API } from '../../../../api/api';
import { URLS } from '../../../../api/urls';
import { Modal } from '../../../../components/Modal/Modal';
import { ALERT_TEXT } from '../../../../constants/content';
import { useAlert } from '../../../../context/Alert.context';
import { GadgetDto } from '../../../../types/gadgets.types';
import { GadgetEdit, GadgetSeoEdit } from '../../GadgetsPage.type';
import { GadgetForm } from './GadgetForm';

interface CreateGadgetModalProps {
  open: boolean;
  onClose: () => void;
  gadget: GadgetEdit;
  seoDefaultValue?: GadgetSeoEdit | null;
  updateGadgetAfterEdit: (data: GadgetDto) => void;
}

export const EditGadgetModal = ({
  open,
  onClose,
  gadget,
  updateGadgetAfterEdit,
  seoDefaultValue
}: CreateGadgetModalProps) => {
  const [loading, setLoading] = useState(false);
  const { showAlert } = useAlert();

  const edit = async (data: GadgetEdit, seo: GadgetSeoEdit) => {
    try {
      setLoading(true);
      const formData = new FormData();
      data?.fileInfo && formData.append('fileInfo', data.fileInfo as File);
      formData.append('slug', data.slug || '');
      formData.append('name', data.name || '');
      formData.append('icon', data.icon || '');
      formData.append('faqPl', JSON.stringify(data.faqPl ?? []));
      formData.append('faqEn', JSON.stringify(data.faqEn ?? []));
      formData.append('faqUk', JSON.stringify(data.faqUk ?? []));
      formData.append('faqRu', JSON.stringify(data.faqRu ?? []));
      const { data: updatedGadget } = await API.put(
        `${URLS.GADGETS}/${gadget.id}`,
        formData,
      );

      updateGadgetAfterEdit(updatedGadget);
      showAlert({
        message: ALERT_TEXT.GADGET_EDIT_SUCCESS,
        severity: 'success',
      });

      if (seoDefaultValue?.id) {
        try {
          await API.put(
            `${URLS.GADGET_SEO}/${seoDefaultValue.id}`,
            seo,
          );
          showAlert({
            message: ALERT_TEXT.GADGET_SEO_EDIT_SUCCESS,
            severity: 'success',
          });
        } catch (e: any) {
          showAlert({
            message: e?.message ?? 'Failed to update gadget SEO',
            severity: 'error',
          });
        }
      } else {
        try {
          await API.post(URLS.GADGET_SEO, {
            gadgetId: gadget.id,
            ...seo,
          });
          showAlert({
            message: ALERT_TEXT.GADGET_SEO_EDIT_SUCCESS,
            severity: 'success',
          });
        } catch (e: any) {
          showAlert({
            message: e?.message ?? 'Failed to update gadget SEO',
            severity: 'error',
          });
        }
      }
      onClose();
    } catch (e: any) {
      showAlert({
        message: e?.response?.data?.response ?? 'Failed to update gadget',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal shouldPreventModal title="Edit gadget" open={open} onClose={onClose}>
      <GadgetForm
        loading={loading}
        onSubmit={edit}
        onCancel={onClose}
        defaultValue={gadget}
        seoDefaultValue={seoDefaultValue}
      />
    </Modal>
  );
};
